import { Check, ChevronsUpDown, X } from 'lucide-react'
import * as React from 'react'

import { Badge } from '@/components/ui/common/Badge'
import { Button } from '@/components/ui/common/Button'
import { Command, CommandGroup, CommandItem } from '@/components/ui/common/Command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/common/Popover'
import { cn } from '@/utils/common'
import { inputVariants } from '../text/Input'

export interface Option {
  label: string
  value: '1536x1024' | '1024x1536' | '1024x1024'
}

interface MultiselectProps {
  options: Option[]
  selected: Option[]
  onChange: (options: Option[]) => void
  className?: string
  placeholder?: string
  error?: boolean
}

export function Multiselect({
  options,
  selected,
  onChange,
  className,
  placeholder = 'Select options...',
  error
}: MultiselectProps) {
  const [open, setOpen] = React.useState(false)

  const safeOptions = Array.isArray(options) ? options : []
  const safeSelected = Array.isArray(selected) ? selected : []

  const handleUnselect = (option: Option) => {
    onChange(safeSelected.filter((item) => item.value !== option.value))
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className={cn(
            inputVariants({ variant: 'default' }), // Apply base input styling
            'w-full justify-between font-normal', // Ensure text is not bold by default
            'focus:border-primary focus:ring-primary data-[state=open]:ring-primary', // Match focus and open state styling
            error ? 'ring-1 !ring-destructive' : '', // Match error styling
            className
          )}
          role="combobox"
          variant="outline"
        >
          <div className="scrollbar-hide flex-1 overflow-x-auto">
            <div className="flex items-center gap-1">
              {safeSelected.length > 0 ? (
                safeSelected.map((option) => (
                  <Badge
                    className="mr-1 bg-gray-50 whitespace-nowrap text-black hover:bg-gray-200"
                    key={option.value}
                    variant="secondary"
                  >
                    {option.label}
                    <button
                      className="ml-1 rounded-full ring-offset-background outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
                      onClick={() => handleUnselect(option)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUnselect(option)
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      <X className="h-3 w-3 text-gray-500 hover:text-gray-900" />
                    </button>
                  </Badge>
                ))
              ) : (
                <span className="text-gray-300">{placeholder}</span>
              )}
            </div>
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-xs p-0 shadow-md">
        <Command className="border border-gray-200">
          <CommandGroup className="max-h-64 overflow-auto">
            {safeOptions.map((option) => (
              <CommandItem
                className="justify-between rounded-lg text-sm hover:bg-gray-100"
                key={option.value}
                onSelect={() => {
                  onChange(
                    safeSelected.some((item) => item.value === option.value)
                      ? safeSelected.filter((item) => item.value !== option.value)
                      : [...safeSelected, option]
                  )
                  setOpen(true)
                }}
              >
                {option.label}
                <div className="flex aspect-square items-center justify-center border border-border">
                  <Check
                    className={cn(
                      'h-4 w-4 text-gray-900',
                      safeSelected.some((item) => item.value === option.value)
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
